import { type AuthUser } from "wasp/auth";
import { useState, ReactNode, FC, useEffect, useRef, useMemo } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCurrentUser, setCurrentCollection } from "../../store/userSlice";
import {
  useQuery,
  getUserById,
  getAllCollectionsOfUser,
  getEnrichmentTasks,
  updateCurrentUser,
  getAllPlans,
  createUserSubscription,
} from "wasp/client/operations";
import GetStartedDialog from "../../dialogs/GetStartedDialog";
import BetterDocsWidget from "../../client/components/BetterDocsWidget";
import { StartEnrichmentDialog } from "../../dialogs/StartEnrichmentDialog";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  setEnrichmentDialogOpen,
  setLastUnsyncedJob,
  initializePendingChanges,
  setReconnectDialogOpen,
  setReimportExcelDialogOpen,
  setIsAddStoreDialogOpen,
} from "../../store/flowSlice";
import { ApiProvider } from "../../hooks/useApi";
import { EnrichmentJob } from "wasp/entities";
import { updateMiqCollection } from "wasp/client/operations";
import { Toaster } from "../../components/ui/toaster";
import useSendInitialScoreMail from "../../hooks/useSendInitialScoreMail";
import { useApi } from "../../hooks/useApi";
import { setInitialQualityScore } from "../../store/score/scoreSlice";
import ReconnectDialog from "../../dialogs/ReconnectDialog";
import { chargebeeBackendBaseUrl, shopifyBackendBaseUrl } from "../dashboards/urls";
import { GetAllCollectionsOfUserOutput } from "../dashboards/operations";
import { ExportJobService } from "../../services/job/ExportJobService";
import { GenericJobService } from "../../services/job/GenericJobService";
import { analytics } from "../../analytics";
import ExcelReimportDialog from "../../dialogs/ExcelReimportDialog";
import AddStoreDialog from "../../dialogs/AddStoreDialog";
import { getDefaultCollectionName } from "../../utils";
import SwitchStoreDialog from "../../dialogs/SwitchStoreDialog";
import { setSwitchStoreDialogOpen } from "../../store/flowSlice";
import { DebugCommandView } from "../../debugCommandView";
import GlobalToast from "../../GlobalToast";

interface Props {
  user: AuthUser;
  children?: ReactNode;
  rightSidebarContent?: ReactNode;
  useWideLayout?: boolean;
}

const DefaultLayout: FC<Props> = ({
  children,
  user,
  rightSidebarContent,
  useWideLayout = false,
}) => {
  useSendInitialScoreMail();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: userUpdate, refetch: refetchUser } = useQuery(getUserById, {
    id: user.id,
  });
  const { data: collections, refetch: refetchCollections } = useQuery(
    getAllCollectionsOfUser
  );
  const {
    data: plans,
    isLoading: plansLoading,
    error: plansError,
  } = useQuery(getAllPlans);
  const api = useApi();

  const { data: enrichmentTasks_, refetch: refetchEnrichmentTasks } =
    useQuery(getEnrichmentTasks);
  const enrichmentTasks: EnrichmentJob[] = enrichmentTasks_ as EnrichmentJob[];

  const {
    isEnrichmentDialogOpen,
    enrichmentPendingChanges,
    reconnectDialogOpen,
    isReimportExcelDialogOpen,
    isAddStoreDialogOpen,
    isSwitchStoreDialogOpen
  } = useSelector((state: RootState) => state.flow);

  const { currentCollection } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    if (plansLoading === false && plans && plans.length > 0) {
      const urlParams = localStorage.getItem("urlParams");
      //const isDebugMode = localStorage.getItem("debug_mode");
      const chargeId = urlParams ? JSON.parse(urlParams)?.charge_id : null;
      const chargebeeId = urlParams ? JSON.parse(urlParams)?.id : null;
      const state = urlParams ? JSON.parse(urlParams)?.state : null;
      if(chargeId){
        controlChargeId(chargeId);
      } else if(chargebeeId && state && state === "succeeded"){
        controlChargebeeId(chargebeeId, state);
      }
    }
  }, [plansLoading]);

  async function controlChargeId(chargeId: string) {
    if (chargeId) {
      analytics.sendPlanUpgredeFlowCompletedEvent({
        email: user.email ?? user.id,
      })
      const url = `${shopifyBackendBaseUrl}/getChargeDetails`;
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          shopDomain: currentCollection?.collection?.miqShopifyDomain,
          app: "miq",
          chargeId: chargeId,
        }),
      })
        .then(async (response) => {
          if (response.ok) {
            const data = (await response.json()) as {
              success: boolean;
              charge: {
                id: string;
                name: string;
                price: number;
                status: string;
                billingOn: string;
                test: boolean;
                createdAt: string;
                trialDays: number;
                interval: string;
              };
            };
            if (plansLoading === false && plans && plans.length > 0) {
              const interval =
                data.charge.interval === "EVERY_30_DAYS" ? "MONTHLY" : "YEARLY";
              const selectedPlan = plans.find(
                (plan: any) =>
                  plan.type === data.charge.name.toUpperCase() &&
                  plan.billingInterval === interval
              );
              createSubscription(selectedPlan.id, data.charge);
            }
          }
        })
        .finally(() => {
          const url = new URL(window.location.href);
          localStorage.removeItem("urlParams");
          url.search = "";
          window.history.replaceState({}, "", url.toString());
        })
        .catch((error) => {
          console.error("Error creating subscription:", error);
        });
    }
  }

  async function controlChargebeeId(chargebeeId: string, state: string) {
    if (state === "succeeded" && chargebeeId) {
      fetch(`${chargebeeBackendBaseUrl}/hosted-page/${chargebeeId}`)
        .then(async (response) => {
          if (response.ok) {
            const data = await response.json();

            // Chargebee'den dönen veriyi kullanarak abonelik oluştur
            if (data.success && data.subscription) {
              const subscriptionData = data.subscription;
              const planId = subscriptionData.subscription_items[0].item_price_id;

              // Planı bul
              const planIdParts = planId.split("-");
              const planType = planIdParts[0].toUpperCase();
              const interval = planIdParts[2].toUpperCase();

              const selectedPlan = plans.find(
                (plan: any) =>
                  plan.type === planType && plan.billingInterval === interval
              );
              if (selectedPlan) {
                // Chargebee verilerini Shopify formatına dönüştür
                const charge = {
                  id: subscriptionData.id,
                  name: selectedPlan.name,
                  price: subscriptionData.subscription_items[0].unit_price,
                  status: subscriptionData.status.toUpperCase(),
                  billingOn: new Date(
                    subscriptionData.next_billing_at * 1000
                  ).toISOString(),
                  test: false,
                  createdAt: new Date(
                    subscriptionData.created_at * 1000
                  ).toISOString(),
                  trialDays: 0,
                  interval: interval,
                  metadata: {
                    subscriptionId: subscriptionData.id,
                  },
                };
                // Abonelik oluştur
                createSubscription(selectedPlan.id, charge);

                // Başarılı işlem sonrası yönlendirme
                //window.location.href = "/admin/dashboard";
              }
            }
          } else {
            console.error("Abonelik bilgileri alınamadı");
          }
        })
        .finally(() => {
          // URL'den parametreleri temizle
          localStorage.removeItem("urlParams");
          const url = new URL(window.location.href);
          url.search = "";
          window.history.replaceState({}, "", url.toString());
        })
        .catch((error) => {
          console.error("Abonelik kontrolü sırasında hata:", error);
        });
    }
  }

  async function createSubscription(
    id: number,
    charge: {
      id: string;
      name: string;
      price: number;
      status: string;
      billingOn: string;
      test: boolean;
      createdAt: string;
      trialDays: number;
      interval: string;
      metadata?: {
        subscriptionId: string;
      };
    }
  ) {
    await createUserSubscription({
      userId: user.id,
      planId: id,
      charge: charge,
    });
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const storedParams = Object.fromEntries(params.entries());
    if (Object.keys(storedParams).length > 0) {
      localStorage.setItem("urlParams", JSON.stringify(storedParams));
    }
  }, []);

  const { data, isLoading } = api.useQualityScore(currentCollection?.collection?.tableId!);
  const qualityScore = data?.data;

  useEffect(() => {
    if (qualityScore) {
      dispatch(setInitialQualityScore(qualityScore));
    }
  }, [qualityScore]);

  useEffect(() => {
    if (enrichmentTasks && enrichmentTasks.length > 0) {
      const lastUnsyncedJob = (() => {
        // First sort all tasks by endedAt date
        const sortedTasks = [...enrichmentTasks].sort(
          (a, b) => new Date(b.endedAt!).getTime() - new Date(a.endedAt!).getTime()
        );
  
        // Find the first unsynced job
        const firstUnsynced = sortedTasks.find(
          (task) =>
            task.status === "completed" &&
            task.isExportingToShopify !== true &&
            !task.syncedToShopify &&
            !task.syncMessageDismissed
        );
  
        // If no unsynced job found, return null
        if (!firstUnsynced) return null;
  
        // Find the most recent job's date
        const mostRecentJobDate = sortedTasks[0]?.endedAt;
        
        // If the most recent job is different from our unsynced job and is synced,
        // return null, otherwise return the unsynced job
        return mostRecentJobDate !== firstUnsynced.endedAt ? null : firstUnsynced;
      })();
  
      dispatch(setLastUnsyncedJob(lastUnsyncedJob));
    } else {
      dispatch(setLastUnsyncedJob(null));
    }
  }, [enrichmentTasks]);

  const initializedRef = useRef(false);
  const updatingRef = useRef(false);

  useEffect(() => {
    if (
      !initializedRef.current &&
      currentCollection?.collection?.pendingEnrichmentChangesJSON
    ) {
      try {
        initializedRef.current = true;
        const savedChanges = JSON.parse(
          currentCollection?.collection?.pendingEnrichmentChangesJSON
        );
        dispatch(initializePendingChanges(savedChanges));
      } catch (err) {
        console.error("Failed to parse pending changes from DB:", err);
      }
    }
  }, [currentCollection?.collection?.id]);

  useEffect(() => {
    if (!currentCollection?.collection?.id || updatingRef.current) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        updatingRef.current = true;
        if (!currentCollection?.collection?.id) {
          return;
        }
        await updateMiqCollection({
          id: currentCollection?.collection?.id,
          pendingEnrichmentChangesJSON:
            enrichmentPendingChanges.length > 0
              ? JSON.stringify(enrichmentPendingChanges)
              : null,
        });
      } finally {
        updatingRef.current = false;
      }
    }, 1000); // 1 second debounce

    return () => clearTimeout(timeoutId);
  }, [enrichmentPendingChanges]);

  useEffect(() => {
    if (userUpdate) {
      dispatch(setCurrentUser(userUpdate as AuthUser));
    }
  }, [userUpdate]);


  useEffect(() => {
    if (user?.currentCollectionId && collections && Array.isArray(collections)) {
      const currentCollection = (collections as GetAllCollectionsOfUserOutput[]).find((collection) => collection?.id === user.currentCollectionId);
      if (currentCollection && typeof currentCollection === 'object') {
        dispatch(setCurrentCollection(currentCollection));
      } else if (collections.length > 0 && typeof collections[0] === 'object') {
        dispatch(setCurrentCollection((collections as any[])[0]));
      }
    }
  }, [user, collections])
 
  useEffect(() => {
    if (!user) return;
    if (!user.didCompleteOnboarding && !user.didSkipOnboarding) {
      analytics.sendOnboardingStartedEvent({
        email: user.email ?? user.id,
      });
      navigate("/admin/onboarding");
    }
  }, [user]);

  const exportJobs = useSelector((state: RootState) => state.jobs.activeExportJobs);
  useEffect(() => {
    ExportJobService.getInstance().ensurePolling();

    return () => {
      if(Object.keys(exportJobs).length === 0){
        // console.log("Stopping polling since there is no export job.")
        ExportJobService.getInstance().stopPolling();
      }
    }
  }, [exportJobs])

  const jobs = useSelector((state: RootState) => state.jobs.activeGenericJobs);
  useEffect(() => {
    GenericJobService.getInstance().ensurePolling();

    return () => {
      if(Object.keys(jobs).length === 0){
        // console.log("Stopping polling since there is no generic job.")
        GenericJobService.getInstance().stopPolling();
      }
    }
  }, [jobs])

  return (
    <>
    <DebugCommandView/>
      <ReconnectDialog
        open={reconnectDialogOpen}
        onOpenChange={(isOpen) => {
          dispatch(setReconnectDialogOpen(isOpen));
        }}
      />
      <GetStartedDialog />
      
      <ExcelReimportDialog
        open={isReimportExcelDialogOpen}
        onOpenChange={(isOpen) => {
          dispatch(setReimportExcelDialogOpen(isOpen));
        }}
      />

      <AddStoreDialog
        open={isAddStoreDialogOpen}
        onOpenChange={(isOpen) => {
          dispatch(setIsAddStoreDialogOpen(isOpen));
        }
      }
      />

      <SwitchStoreDialog
        open={isSwitchStoreDialogOpen}
        onOpenChange={(isOpen) => {
          dispatch(setSwitchStoreDialogOpen(isOpen));
        }}
      />

      <StartEnrichmentDialog
        open={isEnrichmentDialogOpen}
        onOpenChange={(isOpen) => {
          dispatch(
            setEnrichmentDialogOpen({
              isOpen,
            })
          );
        }}
      />
      <BetterDocsWidget />
      <div className="bg-[#1A1A1A] h-[100vh] dark:bg-boxdark-2 dark:text-bodydark overflow-hidden relative">
        <Header
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          user={user}
        />
        <div
          className="flex overflow-hidden rounded-[16px] mx-[4px] bg-[#F1F1F1] dark:bg-[#050608]"
          style={{
            height: "calc(100vh - 65px)",
          }}
        >
          <Sidebar
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
          />
          <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden min-w-0">
            <main className="flex-1">
              <div
                className={`
                mx-auto w-full 
                p-1 md:p-6 2xl:p-8
                ${useWideLayout ? "max-w-screen-2xl" : "max-w-screen-lg"}
              `}
              >
                <div
                  className={`
                  w-full min-w-0
                  ${useWideLayout ? "max-w-full" : ""}
                `}
                >
                  {currentCollection?.collection && <ApiProvider>{children}</ApiProvider>}
                </div>
              </div>
            </main>
          </div>
          {rightSidebarContent && (
            <div
              className={`
              hidden md:block border-l bg-[#F1F1F1] 
              overflow-y-auto flex-shrink-0
              transition-all duration-300
              ${useWideLayout ? "w-[320px]" : "w-[300px]"}
              px-4 md:px-5 2xl:px-6
              h-full
            `}
            >
              <div className="min-w-[300px]">{rightSidebarContent}</div>
            </div>
          )}
        </div>
      </div>
      <Toaster />
      <GlobalToast/>

    </>
  );
};

export default DefaultLayout;
